'use client';

import type { FC, ReactNode } from 'react';
import { BottomSheet } from './ui/BottomSheet';
import { cn } from '../utils/cn';

export type RulesSheetTint = 'amber' | 'green' | 'blue' | 'sky' | 'violet' | 'emerald';

const TINT: Record<RulesSheetTint, string> = {
  amber: 'bg-price/15 text-price',
  green: 'bg-accent-green/15 text-accent-green',
  blue: 'bg-accent-blue/15 text-accent-blue',
  sky: 'bg-sky-400/15 text-sky-400',
  violet: 'bg-violet-400/15 text-violet-400',
  emerald: 'bg-accent-green/15 text-accent-green',
};

export type RulesSheetVariant = 'card' | 'compact' | 'plain' | 'text';

interface VariantShape {
  list: string;
  row: string;
  icon?: string;
  column?: string;
  title: string;
  body: string;
}

const VARIANT: Record<RulesSheetVariant, VariantShape> = {
  card: {
    list: 'flex flex-col gap-3',
    row: 'flex items-start gap-3 rounded-2xl bg-fill-tertiary p-4',
    icon: 'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-xl',
    column: 'min-w-0 flex-1',
    title: 'text-base font-bold text-label-primary',
    body: 'mt-1 text-sm leading-normal text-label-secondary',
  },
  compact: {
    list: 'flex flex-col gap-3',
    row: 'flex items-start gap-3 rounded-2xl bg-fill-tertiary p-4',
    icon: 'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-xl',
    column: 'min-w-0 flex-1',
    title: 'text-sm font-bold text-label-primary',
    body: 'mt-0.5 text-sm leading-normal text-label-secondary',
  },
  plain: {
    list: 'space-y-4',
    row: 'flex gap-3',
    icon: 'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
    column: 'min-w-0 flex-1 pt-0.5',
    title: 'text-sm font-semibold text-label-primary',
    body: 'mt-0.5 text-[13px] leading-snug text-label-secondary',
  },
  text: {
    list: 'flex flex-col gap-4',
    row: 'flex flex-col gap-1 rounded-2xl bg-bg-secondary p-4',
    title: 'text-[15px] font-semibold leading-normal text-label-primary',
    body: 'text-sm font-normal leading-snug text-label-secondary',
  },
};

export interface RulesSheetRow {
  icon?: ReactNode;
  tint?: RulesSheetTint;
  title: ReactNode;
  body: ReactNode;
  key?: string;
}

export interface RulesSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  rows: RulesSheetRow[];
  variant?: RulesSheetVariant;
  intro?: ReactNode;
  footer?: ReactNode;
  gapClassName?: string;
  className?: string;
  heightClass?: string;
}

export const RulesSheet: FC<RulesSheetProps> = ({
  isOpen,
  onClose,
  title,
  rows,
  variant = 'card',
  intro,
  footer,
  gapClassName = 'gap-3',
  className,
  heightClass = 'h-auto max-h-[88vh]',
}) => {
  const shape = VARIANT[variant];

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={title} heightClass={heightClass}>
      <div className={cn('flex flex-col pb-6', gapClassName, className)}>
        {intro}

        <div className={shape.list}>
          {rows.map((row, index) => (
            <div key={row.key ?? index} className={shape.row}>
              {shape.icon && row.icon ? (
                <div className={cn(shape.icon, TINT[row.tint ?? 'amber'])}>{row.icon}</div>
              ) : null}
              {shape.column ? (
                <div className={shape.column}>
                  <h3 className={shape.title}>{row.title}</h3>
                  <p className={shape.body}>{row.body}</p>
                </div>
              ) : (
                <>
                  <h3 className={shape.title}>{row.title}</h3>
                  <p className={shape.body}>{row.body}</p>
                </>
              )}
            </div>
          ))}
        </div>

        {footer}
      </div>
    </BottomSheet>
  );
};

export default RulesSheet;
